<template>
  <v-menu
    v-model="menuState"
    :attach="attach()"
    :close-on-content-click="false"
    :nudge-right="20"
    :disabled="disabled"
    allow-overflow
    transition="scale-transition"
    offset-y
    min-width="290px"
  >
    <template #activator="{ on, attrs }">
      <div>
        <v-text-field
          v-model="formattedDate"
          input-ref="input"
          v-bind="{
            attrs,
            label,
            hint,

            errorMessages,
            disabled,
            readOnly,
            clearable,
            autofocus,
            hideDetails,
            persistentHint,
            dense
          }"
          :class="inputClass"
          :data-test="dataTest"
          append-icon="$iconCalendar"
          outlined
          v-on="{ ...on }"
          @keyup.enter="$event => confirmVal($event)"
          @blur="$event => confirmVal($event)"
        >
          <template v-if="$vuetify.breakpoint.smAndDown" #append>
            <v-icon v-on="on">$iconCalendar</v-icon>
          </template>
          <template #append-outer><slot name="append-outer" /></template>
        </v-text-field>
      </div>
    </template>
    <v-date-picker
      v-model="valueDate"
      :max="max"
      :min="min"
      :readonly="readOnly"
      :locale="locale"
      no-title
      first-day-of-week="1"
      @input="() => (menuState = false)"
    />
  </v-menu>
</template>
<script>
import {
  defineComponent,
  ref,
  computed,
  watch,
  onBeforeMount,
  onBeforeUnmount,
  nextTick,
  toRef
} from "vue";
import i18next from "i18next";
import dayjs from "@/plugins/dayjs";
import { DateFormats } from "@/utils/formats";

export default defineComponent({
  name: "AppDatePicker",
  props: {
    value: {
      type: String,
      default: null
    },

    label: {
      type: String,
      default() {
        return this.$t(
          "common:label.date",
          "Date",
          "Generic label for date selection. Used in places where no need for more specifc label"
        );
      }
    },
    hint: {
      type: String,
      default: null
    },
    persistentHint: {
      type: Boolean,
      default: false
    },

    attach: {
      type: Function,
      default: () => undefined
    },

    min: {
      type: String,
      default: null
    },
    max: {
      type: String,
      default: null
    },

    clearable: {
      type: Boolean,
      default: true
    },

    disabled: {
      type: Boolean,
      default: false
    },
    hideDetails: {
      type: [Boolean, String],
      default: false
    },
    autofocus: {
      type: Boolean,
      default: false
    },
    errorMessages: {
      type: [String, Array],
      default: function () {
        return [];
      }
    },
    readOnly: {
      type: Boolean,
      default: false
    },
    dense: {
      type: Boolean,
      default: false
    },

    inputClass: {
      type: [String, Object],
      default() {
        return {};
      }
    },
    dataTest: {
      type: String,
      default: "datePicker"
    }
  },
  setup(props, ctx) {
    const inputVal = toRef(props, "value");
    const menuState = ref(false);
    const formattedDate = ref(null);

    const locale = computed(() => dayjs.locale());
    const valueDate = computed({
      get() {
        // Prevent the nuisance of user opening picker and see its on year 2199
        if (inputVal.value === "2199-01-01") {
          return null;
        }
        return inputVal.value;
      },
      set(newDate) {
        if (!newDate) {
          formattedDate.value = null;
          ctx.emit("input", null);
          return;
        }
        const date = dayjs(newDate, DateFormats, true);
        if (date.isValid()) {
          ctx.emit("input", date.format("YYYY-MM-DD"));
          formattedDate.value = dayjs.date(newDate);
          return;
        }
        formattedDate.value = dayjs.date(inputVal.value);
      }
    });

    if (inputVal.value?.includes("T")) {
      ctx.emit("input", inputVal.value.split("T")[0]);
    }

    /**
     * When value prop gets changed externally
     */
    watch(
      inputVal,
      newDate => {
        // Show dots instead of that year date
        if (inputVal.value === "2199-01-01") {
          formattedDate.value = "...";
          return;
        }

        // Including T means its DateTime format. Make it into Date format
        if (newDate?.includes("T")) {
          newDate = newDate.split("T")[0];
        }

        const date = dayjs(newDate, DateFormats, true);
        if (!newDate || !date.isValid()) {
          formattedDate.value = null;
          if (newDate) ctx.emit("input", null);
          return;
        }
        // Update formattedDate if props gets new date that's not been trough valueDate
        formattedDate.value = dayjs.date(newDate);
      },
      { immediate: true }
    );

    function refreshLocaleDate() {
      nextTick(() => {
        valueDate.value = props.value;
      });
    }

    function confirmVal({ target, type }) {
      let value = inputVal.value;
      if (target.value) value = target.value;
      else if (target.value.length == 0 && props.clearable) value = null;

      valueDate.value = value;

      if (type != "blur") {
        menuState.value = false;
      } else {
        ctx.emit("blur");
      }
    }

    onBeforeMount(() => {
      i18next.on("languageChanged", refreshLocaleDate);
    });

    onBeforeUnmount(() => {
      i18next.off("languageChanged", refreshLocaleDate);
    });

    return {
      menuState,
      formattedDate,

      locale,
      valueDate,
      confirmVal
    };
  }
});
</script>
